if (!customElements.get('hero-banner')) {
  customElements.define(
    'hero-banner',
    class HeroBanner extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        const slideCount =
          parseInt(this.dataset.slideCount, 10) || this.querySelectorAll('.hero-banner-block').length;
        if (slideCount > 1) {
          this.whenSwiperReady(() => this.initSlider());
        }
        this.initBannerLinks();
        this.initCountdowns();
        this.setBottomTitle();
      }

      whenSwiperReady(callback) {
        const run = () => {
          if (typeof Swiper === 'undefined') return false;
          callback();
          return true;
        };

        if (run()) return;

        let attempts = 0;
        const intervalId = window.setInterval(() => {
          attempts += 1;
          if (run() || attempts >= 80) window.clearInterval(intervalId);
        }, 100);

        window.addEventListener('load', () => run(), { once: true });
      }

      getSlideCount() {
        const domCount = this.querySelectorAll('.hero-banner-block').length;
        const dataCount = parseInt(this.dataset.slideCount, 10);
        return domCount || dataCount || 0;
      }

      getSwiperSlideIndex(swiper) {
        return swiper.params.loop ? swiper.realIndex : swiper.activeIndex;
      }

      goToSwiperSlide(swiper, index) {
        if (!swiper || index < 0) return;
        if (swiper.params.loop) {
          swiper.slideToLoop(index);
        } else {
          swiper.slideTo(index);
        }
      }

      unveilSlideMedia(slideEl) {
        if (!slideEl) return;

        slideEl.querySelectorAll('source[data-srcset]').forEach((source) => {
          if (source.dataset.srcset) {
            source.setAttribute('srcset', source.dataset.srcset);
            source.removeAttribute('data-srcset');
          }
        });

        if (typeof lazysizes !== 'undefined') {
          slideEl.querySelectorAll('img.lazyload').forEach((img) => {
            lazysizes.loader.unveil(img);
          });
        }
      }

      initSlider() {
        const sectionId = this.dataset.sectionId;
        const swiperEl =
          sectionId
            ? this.querySelector(`#swiper-banner-${sectionId}`)
            : this.querySelector('[id^="swiper-banner-"]');
        if (!swiperEl || swiperEl.swiper || typeof Swiper === 'undefined') return;

        const slideCount = this.getSlideCount();
        const autoplayEnabled = this.dataset.autoplay === 'true';
        const transitionSpeed = parseInt(this.dataset.transitionSpeed, 10) || 5;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        // Swiper loop duplicates slides; with only 2 real slides it often lands on the wrong clone.
        const useLoop = slideCount >= 3;

        const swiperConfig = {
          direction: 'horizontal',
          slidesPerView: 1,
          loop: useLoop,
          // No rewind on 2 slides: on the last slide, swiping "next" must not jump to slide 1.
          rewind: false,
          allowTouchMove: true,
          grabCursor: !isMobile,
          watchOverflow: false,
          resistance: true,
          resistanceRatio: 0.85,
          observer: true,
          observeParents: true,
          passiveListeners: true,
          touchStartPreventDefault: false,
        };

        if (useLoop) {
          swiperConfig.loopAdditionalSlides = 1;
          swiperConfig.loopedSlides = slideCount;
        }

        if (sectionId) {
          const nextNav = this.querySelector(`.rocket-button-next_${sectionId}`);
          const prevNav = this.querySelector(`.rocket-button-prev_${sectionId}`);
          if (nextNav && prevNav) {
            swiperConfig.navigation = {
              nextEl: `.rocket-button-next_${sectionId}`,
              prevEl: `.rocket-button-prev_${sectionId}`,
            };
          }
        }

        const swiper = new Swiper(swiperEl, swiperConfig);
        this.swiperInstance = swiper;
        this.initCustomPagination(swiper);

        requestAnimationFrame(() => {
          swiper.update();
        });

        const onSlideActivate = () => {
          const activeSlide = swiper.slides[swiper.activeIndex];
          this.unveilSlideMedia(activeSlide);
          const nextSlide = swiper.slides[swiper.activeIndex + 1];
          const prevSlide = swiper.slides[swiper.activeIndex - 1];
          this.unveilSlideMedia(nextSlide);
          this.unveilSlideMedia(prevSlide);
          this.setBottomTitle();
        };

        swiper.on('slideChangeTransitionStart', onSlideActivate);
        onSlideActivate();

        let lastWidth = swiperEl.offsetWidth;
        const onWidthChange = () => {
          const width = swiperEl.offsetWidth;
          if (width === lastWidth) return;
          lastWidth = width;
          swiper.update();
        };
        window.addEventListener('resize', onWidthChange);
        this._swiperLayoutHandler = onWidthChange;

        if (autoplayEnabled) {
          this.initCustomAutoplay(swiper, transitionSpeed * 1000);
        }
      }

      initCustomAutoplay(swiper, delayMs) {
        this._autoplayPaused = false;
        this._autoplayTimer = null;

        const clearAutoplayTimer = () => {
          if (this._autoplayTimer) {
            clearTimeout(this._autoplayTimer);
            this._autoplayTimer = null;
          }
        };

        const scheduleAutoplay = () => {
          clearAutoplayTimer();
          if (this._autoplayPaused || this.dataset.autoplay !== 'true') return;
          this._autoplayTimer = setTimeout(() => {
            if (!swiper.params.loop && swiper.isEnd) {
              swiper.slideTo(0);
            } else {
              swiper.slideNext();
            }
          }, delayMs);
        };

        const hoverRoot = this.closest('section.rocket-hero-banner') || this;
        hoverRoot.addEventListener('mouseenter', () => {
          this._autoplayPaused = true;
          clearAutoplayTimer();
        });
        hoverRoot.addEventListener('mouseleave', () => {
          this._autoplayPaused = false;
          scheduleAutoplay();
        });

        swiper.on('slideChangeTransitionEnd', scheduleAutoplay);
        scheduleAutoplay();
      }

      initBannerLinks() {
        this.querySelectorAll('.hero-banner-block[data-banner-link]').forEach((slide) => {
          let touchStartX = 0;
          let touchStartY = 0;
          let touchMoved = false;

          slide.addEventListener(
            'touchstart',
            (event) => {
              const touch = event.touches[0];
              if (!touch) return;
              touchStartX = touch.clientX;
              touchStartY = touch.clientY;
              touchMoved = false;
            },
            { passive: true }
          );

          slide.addEventListener(
            'touchmove',
            (event) => {
              const touch = event.touches[0];
              if (!touch) return;
              if (
                Math.abs(touch.clientX - touchStartX) > 8 ||
                Math.abs(touch.clientY - touchStartY) > 8
              ) {
                touchMoved = true;
              }
            },
            { passive: true }
          );

          slide.addEventListener('click', (event) => {
            const swiper = this.swiperInstance || this.querySelector('[id^="swiper-banner-"]')?.swiper;
            if (swiper && swiper.allowClick === false) return;
            if (touchMoved) return;
            if (
              event.target.closest(
                '.rocket-hero-banner__pagination, .rock-button, button, a, [role="button"]'
              )
            ) {
              return;
            }
            const link = slide.dataset.bannerLink;
            if (link) window.location.href = link;
          });
        });
      }

      initCustomPagination(swiper) {
        const slideCount = this.getSlideCount();
        const containers = this.querySelectorAll('.rocket-hero-banner__pagination');
        if (!containers.length || !slideCount) return;

        containers.forEach((container) => {
          container.innerHTML = '';
          for (let i = 0; i < slideCount; i++) {
            const bullet = document.createElement('span');
            bullet.className = 'rocket-hero-banner__pagination-bullet';
            bullet.setAttribute('role', 'button');
            bullet.setAttribute('tabindex', '0');
            bullet.setAttribute('aria-label', `Go to slide ${i + 1}`);
            bullet.addEventListener('click', (event) => {
              event.stopPropagation();
              this.goToSwiperSlide(swiper, i);
            });
            bullet.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                this.goToSwiperSlide(swiper, i);
              }
            });
            container.appendChild(bullet);
          }
        });

        const updateBullets = () => {
          const activeIndex = this.getSwiperSlideIndex(swiper);
          containers.forEach((container) => {
            container.querySelectorAll('.rocket-hero-banner__pagination-bullet').forEach((bullet, index) => {
              const isActive = index === activeIndex;
              bullet.classList.toggle('is-active', isActive);
              bullet.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
          });
        };

        swiper.on('slideChange', updateBullets);
        swiper.on('slideChangeTransitionEnd', updateBullets);
        updateBullets();
        this.setBottomTitle();
      }

      setBottomTitle() {
        this.querySelectorAll('.hero-banner-block').forEach((item) => {
          const content = item.querySelector('.rocket-hero-banner__content-wrapper');
          const button = item.querySelector('.rocket-hero-banner__button-wrapper');
          const countdown = item.querySelector('[data-hero-countdown]');
          const description = item.querySelector('.rocket-hero-banner__content-content');
          const pagination = item.querySelector('.rocket-hero-banner__pagination-wrapper');
          const titleEl = item.querySelector('.rocket-hero-banner__content-title');
          if (!titleEl) return;

          let bottom = 0;
          if (content) {
            bottom = bottom + Number(content.getAttribute('data-bottom'));
          }
          if (pagination) {
            const paginationStyles = window.getComputedStyle(pagination);
            bottom = bottom + pagination.offsetHeight + parseFloat(paginationStyles.marginTop || 0);
          }
          if (button) {
            bottom = bottom + Number(button.offsetHeight) + Number(button.getAttribute('data-top'));
          }
          if (countdown) {
            bottom = bottom + Number(countdown.offsetHeight) + Number(countdown.getAttribute('data-top') || 0);
          }
          if (description) {
            bottom = bottom + Number(description.offsetHeight) + Number(description.getAttribute('data-top'));
          }
          const titlePosition = window.getComputedStyle(titleEl).position;
          if (titlePosition === 'absolute' || titlePosition === 'fixed') {
            titleEl.style.bottom = bottom + 'px';
          }
        });
      }

      parseCountdownEnd(value) {
        if (!value) return NaN;
        const trimmed = value.trim();
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
          const now = new Date();
          const parts = trimmed.split(':').map(Number);
          const target = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            parts[0],
            parts[1],
            parts[2] || 0
          );
          if (target.getTime() <= now.getTime()) {
            target.setDate(target.getDate() + 1);
          }
          return target.getTime();
        }
        return new Date(trimmed.replace(' ', 'T')).getTime();
      }

      startCountdown(el) {
        const endDate = this.parseCountdownEnd(el.getAttribute('data-end-date'));
        const daysEl = el.querySelector('[data-unit="days"]');
        const hoursEl = el.querySelector('[data-unit="hours"]');
        const minutesEl = el.querySelector('[data-unit="minutes"]');
        const secondsEl = el.querySelector('[data-unit="seconds"]');
        if (!daysEl || !hoursEl || !minutesEl || !secondsEl || Number.isNaN(endDate)) {
          el.hidden = true;
          return;
        }

        const tick = () => {
          const distance = endDate - Date.now();
          if (distance <= 0) {
            daysEl.textContent = '0';
            hoursEl.textContent = '00';
            minutesEl.textContent = '00';
            secondsEl.textContent = '00';
            return false;
          }
          daysEl.textContent = String(Math.floor(distance / (1000 * 60 * 60 * 24)));
          hoursEl.textContent = String(
            Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
          ).padStart(2, '0');
          minutesEl.textContent = String(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');
          secondsEl.textContent = String(Math.floor((distance % (1000 * 60)) / 1000)).padStart(2, '0');
          return true;
        };

        if (!tick()) return;
        const timer = setInterval(() => {
          if (!tick()) clearInterval(timer);
        }, 1000);
      }

      initCountdowns() {
        this.querySelectorAll('[data-hero-countdown]').forEach((el) => {
          this.startCountdown(el);
        });
      }
    }
  );
}

document.addEventListener('shopify:block:select', (event) => {
  const blockEl = event.target;
  if (!blockEl?.classList?.contains('hero-banner-block')) return;

  const hero = blockEl.closest('hero-banner');
  if (!hero) return;

  const goToBlock = () => {
    const swiperEl = hero.querySelector('[id^="swiper-banner-"]');
    const swiper = hero.swiperInstance || swiperEl?.swiper;
    if (!swiper) return false;

    const slides = hero.querySelectorAll('.hero-banner-block');
    const index = Array.from(slides).indexOf(blockEl);
    if (index < 0) return true;

    if (swiper.params.loop) {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
    return true;
  };

  if (!goToBlock()) {
    hero.whenSwiperReady?.(() => goToBlock());
  }
});
